/**
 * Où mène le clic sur une notification push (correctif du 28/09/2026).
 *
 * Constat de la recette sur iPhone : quand l'application est ouverte en arrière-plan,
 * toucher une notification la ramenait au premier plan sur sa DERNIÈRE page, pas sur la
 * page annoncée. Le service worker ne savait que refocaliser une fenêtre déjà posée
 * exactement sur la cible, sinon il appelait `clients.openWindow`, qui sur mobile se
 * contente de réveiller l'app installée sans la faire naviguer.
 *
 * Ordre suivi, du plus fiable au moins fiable :
 *  1. la fenêtre déjà ouverte reçoit un message et navigue elle-même (routeur React, sans
 *     rechargement), en accusant réception — sans accusé dans le délai, on passe à la suite ;
 *  2. `client.navigate(cible)` (rechargement de la page, mais la bonne) ;
 *  3. aucune fenêtre : `clients.openWindow(cible)`.
 *
 * Ce module ne touche à aucune API globale : le service worker lui passe ce dont il a besoin.
 */

export const TYPE_MESSAGE_NAVIGATION = "NAVIGUER_VERS";

/**
 * Chemin interne (chemin + recherche + ancre) d'une URL de notification, ou `null` si elle
 * sort de l'origine de l'application — une notification ne doit jamais envoyer l'utilisateur
 * hors de Rise-T.
 */
export const cheminInterne = (url, origine) => {
  try {
    const cible = new URL(url || "/", origine);
    if (cible.origin !== origine) return null;
    return `${cible.pathname}${cible.search}${cible.hash}`;
  } catch (erreur) {
    return null;
  }
};

const demanderNavigation = (client, chemin, delaiAccuseMs, creerCanal) =>
  new Promise((resolve) => {
    const canal = creerCanal();
    const minuterie = setTimeout(() => resolve(false), delaiAccuseMs);
    canal.port1.onmessage = () => {
      clearTimeout(minuterie);
      resolve(true);
    };
    try {
      client.postMessage({ type: TYPE_MESSAGE_NAVIGATION, chemin }, [canal.port2]);
    } catch (erreur) {
      clearTimeout(minuterie);
      resolve(false);
    }
  });

/**
 * @param {object} options
 * @param {{ matchAll: Function, openWindow?: Function }} options.clientsApi  `self.clients`
 * @param {string} options.url          URL portée par la notification (`data.url`)
 * @param {string} options.origine      `self.location.origin`
 * @param {number} [options.delaiAccuseMs]
 * @param {() => MessageChannel} [options.creerCanal]  injectable pour les tests (jsdom n'a pas MessageChannel)
 * @returns {Promise<"message"|"navigate"|"open"|"aucune">} le moyen qui a servi
 */
export const ouvrirPageDeNotification = async ({
  clientsApi,
  url,
  origine,
  delaiAccuseMs = 800,
  creerCanal = () => new MessageChannel(),
}) => {
  const chemin = cheminInterne(url, origine);
  if (chemin === null) return "aucune";
  const cible = new URL(chemin, origine).href;

  const fenetres = await clientsApi.matchAll({ type: "window", includeUncontrolled: true });
  const client = fenetres.find((f) => {
    try {
      return new URL(f.url).origin === origine;
    } catch (erreur) {
      return false;
    }
  });

  if (client) {
    try {
      if (typeof client.focus === "function") await client.focus();
    } catch (erreur) {
      // Le focus peut être refusé : la navigation reste utile, l'app est déjà visible ou le sera.
    }

    if (await demanderNavigation(client, chemin, delaiAccuseMs, creerCanal)) return "message";

    if (typeof client.navigate === "function") {
      try {
        await client.navigate(cible);
        return "navigate";
      } catch (erreur) {
        // Navigation refusée (client non contrôlé, navigateur sans support) : dernier recours.
      }
    }
  }

  if (typeof clientsApi.openWindow === "function") {
    await clientsApi.openWindow(cible);
    return "open";
  }
  return "aucune";
};
